import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import FormateurPageShell from './FormateurPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  fetchFormateurProfil,
  fetchFormateurPresenceHistory,
  fetchFormateurSessions,
} from '../../services/academiaFormateurService';
import { formatMoney } from '../../utils/currency';

const QUICK_LINKS = [
  { to: '/formateur/sessions', icon: 'event', title: 'Mes sessions', desc: 'Présences et notes' },
  { to: '/formateur/evaluations', icon: 'quiz', title: 'Évaluations', desc: 'Épreuves, live et corrections' },
  { to: '/formateur/profil', icon: 'person', title: 'Mon profil', desc: 'Parcours et CV' },
  { to: '/formateur/remuneration', icon: 'payments', title: 'Rémunération', desc: 'Prestations et paiements' },
  { to: '/formateur/performance', icon: 'school', title: 'Impact académique', desc: 'Moyennes des cohortes' },
] as const;

type SessionRow = {
  id: string;
  jourSemaine?: string;
  dateSeance?: string | null;
  filiere?: { libelle?: string };
  cohorte?: { code?: string; libelle?: string } | null;
  vacation?: { libelle?: string };
};

type PrestationRow = {
  id: string;
  datePrestation: string;
  statut: string;
  heuresPrestees?: number;
  montantTotalCdf?: number;
};

type PresenceHistoryRow = {
  idSession: string;
  dateSeance: string;
  presencesCount: number;
  archivee: boolean;
  dateArchivage?: string | null;
  filiereLabel?: string | null;
  cohorteCode?: string | null;
};

type EvaluationRow = {
  id: string;
  notePonctualite?: number;
  notePedagogie?: number;
  commentaire?: string | null;
  dateCreation?: string;
  apprenantLabel?: string;
  cohorteCode?: string | null;
};

type ProfilSnapshot = {
  profilFormateur?: { profilComplet?: boolean; specialite?: string };
  performance?: {
    noteMoyenne?: number | null;
    nombreEvaluations?: number;
    tauxSatisfactionPct?: number | null;
  };
  indicateursOperationnels?: {
    seancesPassees?: number;
    seancesAvecPresencesSaisies?: number;
    tauxSaisiePresencesPct?: number | null;
    nombrePrestations?: number;
  };
  synthesePaiement?: {
    montantDuCdf?: number;
    montantPayeCdf?: number;
    heuresPrestees?: number;
    joursPrestes?: number;
  };
  impactAcademique?: {
    synthese?: { moyenneGlobaleSur20?: number | null; nombreCohortes?: number };
  };
  prestations?: PrestationRow[];
  evaluations?: EvaluationRow[];
};

function formatShortDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toNum(v: unknown) {
  if (v == null) return 0;
  return typeof v === 'number' ? v : Number(v) || 0;
}

function sessionSortKey(s: SessionRow) {
  if (s.dateSeance) {
    const t = new Date(s.dateSeance).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return Number.MAX_SAFE_INTEGER;
}

function formateurDisplayName(user: FormateurDashboardPanelProps['user']) {
  return [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ') || user.telephone || 'Formateur';
}

function evalMoyenne(ev: EvaluationRow) {
  if (ev.notePonctualite == null || ev.notePedagogie == null) return null;
  return Math.round(((toNum(ev.notePonctualite) + toNum(ev.notePedagogie)) / 2) * 10) / 10;
}

function prestationStatutLabel(statut: string) {
  const map: Record<string, string> = {
    PLANIFIEE: 'Planifiée',
    REALISEE: 'Réalisée',
    PAYEE: 'Payée',
    ANNULEE: 'Annulée',
  };
  return map[statut] ?? statut;
}

function DashboardWidget({
  id,
  title,
  icon,
  linkTo,
  linkLabel,
  children,
}: {
  id: string;
  title: string;
  icon: string;
  linkTo?: string;
  linkLabel?: string;
  children: ReactNode;
}) {
  return (
    <section className="formateur-dash-widget" aria-labelledby={id}>
      <header className="formateur-dash-widget__header">
        <span className="formateur-dash-widget__header-icon" aria-hidden>
          <LucideSymbolIcon name={icon} tone="inherit" size={20} />
        </span>
        <h2 id={id} className="formateur-dash-widget__title">
          {title}
        </h2>
        {linkTo && linkLabel ? (
          <Link to={linkTo} className="formateur-dash-widget__link">
            {linkLabel}
            <LucideSymbolIcon name="arrow_forward" tone="inherit" size={14} />
          </Link>
        ) : null}
      </header>
      <div className="formateur-dash-widget__body">{children}</div>
    </section>
  );
}

interface FormateurDashboardPanelProps {
  user: {
    telephone?: string;
    mustCompleteProfile?: boolean;
    mustChangePassword?: boolean;
    profil?: { prenom?: string; nom?: string };
  };
}

export default function FormateurDashboardPanel({ user }: FormateurDashboardPanelProps) {
  const [profil, setProfil] = useState<ProfilSnapshot | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [presenceHistory, setPresenceHistory] = useState<PresenceHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const [profilData, sessionRows, historyData] = await Promise.all([
        fetchFormateurProfil(),
        fetchFormateurSessions(),
        fetchFormateurPresenceHistory(),
      ]);
      setProfil(profilData && typeof profilData === 'object' ? (profilData as ProfilSnapshot) : null);
      setSessions(Array.isArray(sessionRows) ? sessionRows : []);
      const feuilles = historyData && typeof historyData === 'object' ? (historyData as { feuilles?: PresenceHistoryRow[] }).feuilles : [];
      setPresenceHistory(Array.isArray(feuilles) ? feuilles : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger votre espace.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useRegisterPortalRefresh(() => void load());

  const montantDu = toNum(profil?.synthesePaiement?.montantDuCdf);
  const nombrePrestations = toNum(profil?.indicateursOperationnels?.nombrePrestations);
  const noteMoyenne = profil?.performance?.noteMoyenne;
  const tauxSaisie = profil?.indicateursOperationnels?.tauxSaisiePresencesPct;
  const seancesPassees = profil?.indicateursOperationnels?.seancesPassees ?? 0;

  const sessionsSansCohorte = useMemo(
    () => sessions.filter((s) => !s.cohorte?.code),
    [sessions],
  );

  const prestationsPlanifiees = useMemo(
    () => (profil?.prestations ?? []).filter((p) => p.statut === 'PLANIFIEE'),
    [profil?.prestations],
  );

  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return [...sessions]
      .filter((s) => {
        if (!s.dateSeance) return true;
        const t = new Date(s.dateSeance).getTime();
        return !Number.isNaN(t) && t >= now;
      })
      .sort((a, b) => sessionSortKey(a) - sessionSortKey(b))
      .slice(0, 3);
  }, [sessions]);

  const recentEvaluations = useMemo(
    () => (profil?.evaluations ?? []).slice(0, 4),
    [profil?.evaluations],
  );

  const recentPrestations = useMemo(
    () =>
      [...(profil?.prestations ?? [])]
        .sort(
          (a, b) =>
            new Date(b.datePrestation).getTime() - new Date(a.datePrestation).getTime(),
        )
        .slice(0, 4),
    [profil?.prestations],
  );

  const recentPresenceSheets = useMemo(() => presenceHistory.slice(0, 5), [presenceHistory]);

  const profilIncomplet =
    Boolean(user.mustCompleteProfile) ||
    profil?.profilFormateur?.profilComplet === false;

  const saisiePresencesFaible =
    seancesPassees > 0 && (tauxSaisie == null || tauxSaisie < 70);

  const todoCount =
    (profilIncomplet ? 1 : 0) +
    (user.mustChangePassword ? 1 : 0) +
    (saisiePresencesFaible ? 1 : 0) +
    Math.min(sessionsSansCohorte.length, 1) +
    Math.min(prestationsPlanifiees.length, 1);

  const moyenneImpact = profil?.impactAcademique?.synthese?.moyenneGlobaleSur20;

  return (
    <FormateurPageShell
      title="Mon espace formateur"
      description="Vue d’ensemble de vos sessions, rémunération et indicateurs pédagogiques."
      sideCard={{
        label: 'Sessions assignées',
        value: loading ? '…' : String(sessions.length),
        helper:
          todoCount > 0
            ? `${todoCount} action${todoCount > 1 ? 's' : ''} à traiter`
            : profil?.profilFormateur?.specialite
              ? profil.profilFormateur.specialite
              : formateurDisplayName(user),
        sideLinks: [
          { label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' },
          { label: 'Rémunération', to: '/formateur/remuneration', icon: 'payments' },
        ],
      }}
      onRetry={() => void load()}
      isLoading={loading}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      loadingMessage="Chargement de votre espace…"
      primaryAction={
        <Link
          to="/formateur/sessions"
          className="inline-flex rounded-lg bg-white px-4 py-2 text-xs font-bold text-primary"
        >
          Saisir présences / notes
        </Link>
      }
    >
      {user.mustCompleteProfile ? (
        <div className="apprenant-dashboard-alert">
          <LucideSymbolIcon name="person" tone="primary" size={18} />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-slate-900">Profil à compléter</p>
            <p className="mt-0.5 text-xs text-on-surface-variant">
              Finalisez votre profil formateur pour accéder à toutes les fonctionnalités.
            </p>
          </div>
          <Link to="/formateur/profil" className="apprenant-dashboard-alert__action">
            Compléter
          </Link>
        </div>
      ) : null}

      <section className="apprenant-dashboard-kpi" aria-label="Indicateurs">
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--primary">
            <LucideSymbolIcon name="event" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Sessions</p>
            <p className="apprenant-dashboard-kpi__value">
              {loading ? '…' : String(sessions.length)}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {sessionsSansCohorte.length > 0
                ? `${sessionsSansCohorte.length} sans cohorte`
                : 'Assignées à vous'}
            </p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--amber">
            <LucideSymbolIcon name="payments" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Montant dû</p>
            <p className="apprenant-dashboard-kpi__value apprenant-dashboard-kpi__value--sm">
              {loading ? '…' : formatMoney(montantDu, 'CDF')}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {loading
                ? '…'
                : `${nombrePrestations} prestation${nombrePrestations > 1 ? 's' : ''} · ${toNum(profil?.synthesePaiement?.heuresPrestees)} h`}
            </p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--sky">
            <LucideSymbolIcon name="star" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Note globale</p>
            <p className="apprenant-dashboard-kpi__value">
              {loading
                ? '…'
                : noteMoyenne != null
                  ? `${toNum(noteMoyenne)}/5`
                  : '—'}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {profil?.performance?.nombreEvaluations != null
                ? `${profil.performance.nombreEvaluations} évaluation(s)`
                : 'Évaluations apprenants'}
            </p>
          </div>
        </article>
        <article className="apprenant-dashboard-kpi__card">
          <span className="apprenant-dashboard-kpi__icon apprenant-dashboard-kpi__icon--emerald">
            <LucideSymbolIcon name="check_circle" tone="inherit" size={20} />
          </span>
          <div>
            <p className="apprenant-dashboard-kpi__label">Saisie présences</p>
            <p className="apprenant-dashboard-kpi__value">
              {loading ? '…' : tauxSaisie != null ? `${tauxSaisie}%` : '—'}
            </p>
            <p className="apprenant-dashboard-kpi__hint">
              {seancesPassees > 0
                ? `${profil?.indicateursOperationnels?.seancesAvecPresencesSaisies ?? 0}/${seancesPassees} feuille${seancesPassees > 1 ? 's' : ''}`
                : 'Aucune feuille enregistrée'}
            </p>
          </div>
        </article>
      </section>

      <section aria-labelledby="formateur-quick-links-heading">
        <h2 id="formateur-quick-links-heading" className="apprenant-dashboard-section-title">
          Accès rapide
        </h2>
        <div className="apprenant-dashboard-quick">
          {QUICK_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="apprenant-dashboard-quick__item">
              <span className="apprenant-dashboard-quick__icon">
                <LucideSymbolIcon name={link.icon} tone="primary" size={20} />
              </span>
              <span className="min-w-0">
                <span className="apprenant-dashboard-quick__title">{link.title}</span>
                <span className="apprenant-dashboard-quick__desc">{link.desc}</span>
              </span>
              <LucideSymbolIcon
                name="arrow_forward"
                tone="inherit"
                size={18}
                className="shrink-0 opacity-40"
              />
            </Link>
          ))}
        </div>
      </section>

      <div className="formateur-dash-grid formateur-dash-grid--2">
        <DashboardWidget
          id="formateur-todo-heading"
          title="À faire"
          icon="checklist"
          linkTo={todoCount > 0 ? '/formateur/sessions' : undefined}
          linkLabel={todoCount > 0 ? 'Sessions' : undefined}
        >
          {todoCount === 0 ? (
            <div className="formateur-dash-empty formateur-dash-empty--success">
              <LucideSymbolIcon name="check_circle" tone="inherit" size={28} />
              <p>{loading ? 'Chargement…' : 'Aucune action urgente. Bon enseignement !'}</p>
            </div>
          ) : (
            <ul className="formateur-dash-todos">
              {profilIncomplet ? (
                <li className="formateur-dash-todo formateur-dash-todo--primary">
                  <span className="formateur-dash-todo__icon">
                    <LucideSymbolIcon name="person" tone="inherit" size={18} />
                  </span>
                  <div className="formateur-dash-todo__content">
                    <p className="formateur-dash-todo__title">Profil incomplet</p>
                    <p className="formateur-dash-todo__desc">
                      Ajoutez votre spécialité, biographie et expérience.
                    </p>
                  </div>
                  <Link to="/formateur/profil" className="formateur-dash-todo__action">
                    Compléter
                  </Link>
                </li>
              ) : null}
              {user.mustChangePassword ? (
                <li className="formateur-dash-todo formateur-dash-todo--amber">
                  <span className="formateur-dash-todo__icon">
                    <LucideSymbolIcon name="lock" tone="inherit" size={18} />
                  </span>
                  <div className="formateur-dash-todo__content">
                    <p className="formateur-dash-todo__title">Mot de passe à changer</p>
                    <p className="formateur-dash-todo__desc">Sécurisez votre compte depuis le profil.</p>
                  </div>
                  <Link to="/formateur/profil" className="formateur-dash-todo__action">
                    Profil
                  </Link>
                </li>
              ) : null}
              {saisiePresencesFaible ? (
                <li className="formateur-dash-todo formateur-dash-todo--amber">
                  <span className="formateur-dash-todo__icon">
                    <LucideSymbolIcon name="check_circle" tone="inherit" size={18} />
                  </span>
                  <div className="formateur-dash-todo__content">
                    <p className="formateur-dash-todo__title">Présences à saisir</p>
                    <p className="formateur-dash-todo__desc">
                      Taux actuel {tauxSaisie ?? 0}% — enregistrez les séances passées.
                    </p>
                  </div>
                  <Link to="/formateur/sessions" className="formateur-dash-todo__action">
                    Saisir
                  </Link>
                </li>
              ) : null}
              {sessionsSansCohorte.slice(0, 2).map((s) => (
                <li key={s.id} className="formateur-dash-todo formateur-dash-todo--warn">
                  <span className="formateur-dash-todo__icon">
                    <LucideSymbolIcon name="warning" tone="inherit" size={18} />
                  </span>
                  <div className="formateur-dash-todo__content">
                    <p className="formateur-dash-todo__title">{s.filiere?.libelle ?? 'Session'}</p>
                    <p className="formateur-dash-todo__desc">Sans cohorte — contactez l’administration.</p>
                  </div>
                </li>
              ))}
              {prestationsPlanifiees.length > 0 ? (
                <li className="formateur-dash-todo">
                  <span className="formateur-dash-todo__icon">
                    <LucideSymbolIcon name="payments" tone="inherit" size={18} />
                  </span>
                  <div className="formateur-dash-todo__content">
                    <p className="formateur-dash-todo__title">Prestations planifiées</p>
                    <p className="formateur-dash-todo__desc">
                      {prestationsPlanifiees.length} en attente de validation.
                    </p>
                  </div>
                  <Link to="/formateur/remuneration" className="formateur-dash-todo__action">
                    Voir
                  </Link>
                </li>
              ) : null}
            </ul>
          )}
        </DashboardWidget>

        <DashboardWidget
          id="formateur-sessions-heading"
          title="Prochaines sessions"
          icon="event"
          linkTo="/formateur/sessions"
          linkLabel="Toutes"
        >
          {upcomingSessions.length === 0 ? (
            <div className="formateur-dash-empty">
              <LucideSymbolIcon name="event_busy" tone="inherit" size={28} />
              <p>
                {loading
                  ? 'Chargement…'
                  : sessions.length === 0
                    ? 'Aucune session assignée pour le moment.'
                    : 'Aucune session à venir planifiée.'}
              </p>
            </div>
          ) : (
            <ul className="formateur-dash-sessions">
              {upcomingSessions.map((s) => (
                <li key={s.id}>
                  <Link to="/formateur/sessions" className="formateur-dash-session">
                    <span className="formateur-dash-session__date">
                      {s.dateSeance ? (
                        formatShortDate(s.dateSeance)
                      ) : (
                        <span>{s.jourSemaine ?? 'À planifier'}</span>
                      )}
                    </span>
                    <span className="formateur-dash-session__body">
                      <span className="formateur-dash-session__title">
                        {s.filiere?.libelle ?? 'Formation'}
                      </span>
                      <span className="formateur-dash-session__meta">
                        <span
                          className={`formateur-dash-session__chip${!s.cohorte?.code ? ' formateur-dash-session__chip--warn' : ''}`}
                        >
                          {s.cohorte?.code ?? 'Sans cohorte'}
                        </span>
                        {s.vacation?.libelle ? (
                          <span className="formateur-dash-session__chip">{s.vacation.libelle}</span>
                        ) : null}
                      </span>
                    </span>
                    <LucideSymbolIcon
                      name="arrow_forward"
                      tone="inherit"
                      size={18}
                      className="formateur-dash-session__arrow"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </DashboardWidget>
      </div>

      <DashboardWidget
        id="formateur-presence-history-heading"
        title="Mes feuilles de présences"
        icon="check_circle"
        linkTo="/formateur/sessions"
        linkLabel="Ouvrir Mes sessions"
      >
        <p className="formateur-dash-empty formateur-dash-empty--inline">
          Pour consulter l’historique : <strong>Mes sessions</strong> → choisir une session → onglet{' '}
          <strong>Présences</strong> → filtres <strong>En cours</strong> ou <strong>Archivées</strong>, puis
          cliquez sur une date pour rouvrir la feuille.
        </p>
        {recentPresenceSheets.length === 0 ? (
          <p className="formateur-dash-empty formateur-dash-empty--inline">
            {loading ? 'Chargement…' : 'Aucune feuille enregistrée pour le moment.'}
          </p>
        ) : (
          <ul className="formateur-dash-prestations">
            {recentPresenceSheets.map((row) => (
              <li key={`${row.idSession}-${row.dateSeance}`} className="formateur-dash-prestation">
                <div className="formateur-dash-prestation__main">
                  <p className="formateur-dash-prestation__date">
                    {formatShortDate(row.dateSeance) ?? '—'}
                  </p>
                  <span
                    className={`formateur-dash-prestation__badge formateur-dash-prestation__badge--${row.archivee ? 'paid' : 'pending'}`}
                  >
                    {row.archivee ? 'Archivée' : 'En cours'}
                  </span>
                </div>
                <p className="formateur-dash-prestation__amount">
                  {row.filiereLabel ?? 'Session'}
                  {row.cohorteCode ? ` · ${row.cohorteCode}` : ''}
                  {' · '}
                  {row.presencesCount} présence{row.presencesCount > 1 ? 's' : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </DashboardWidget>

      <div className="formateur-dash-grid formateur-dash-grid--2">
        <DashboardWidget
          id="formateur-eval-heading"
          title="Dernières évaluations"
          icon="star"
          linkTo="/formateur/profil"
          linkLabel="Mon profil"
        >
          {recentEvaluations.length === 0 ? (
            <div className="formateur-dash-empty">
              <LucideSymbolIcon name="star" tone="inherit" size={28} />
              <p>{loading ? 'Chargement…' : 'Aucune évaluation reçue pour le moment.'}</p>
            </div>
          ) : (
            <ul className="formateur-dash-evals">
              {recentEvaluations.map((ev) => {
                const moy = evalMoyenne(ev);
                return (
                  <li key={ev.id} className="formateur-dash-eval">
                    <span className="formateur-dash-eval__avatar" aria-hidden>
                      {(ev.apprenantLabel ?? 'A').charAt(0).toUpperCase()}
                    </span>
                    <div className="formateur-dash-eval__content">
                      <p className="formateur-dash-eval__name">{ev.apprenantLabel ?? 'Apprenant'}</p>
                      <p className="formateur-dash-eval__meta">
                        {ev.cohorteCode ? (
                          <span className="formateur-dash-eval__cohorte">{ev.cohorteCode}</span>
                        ) : null}
                        {formatShortDate(ev.dateCreation) ? (
                          <span>{formatShortDate(ev.dateCreation)}</span>
                        ) : null}
                      </p>
                      {ev.commentaire ? (
                        <p className="formateur-dash-eval__comment">{ev.commentaire}</p>
                      ) : null}
                    </div>
                    {moy != null ? (
                      <span className="formateur-dash-eval__score">{moy}/5</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </DashboardWidget>

        <DashboardWidget
          id="formateur-remu-heading"
          title="Rémunération & impact"
          icon="payments"
          linkTo="/formateur/remuneration"
          linkLabel="Détail"
        >
          <div className="formateur-dash-finance">
            <div className="formateur-dash-stat formateur-dash-stat--emerald">
              <span className="formateur-dash-stat__icon">
                <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={18} />
              </span>
              <div>
                <p className="formateur-dash-stat__label">Déjà payé</p>
                <p className="formateur-dash-stat__value">
                  {loading ? '…' : formatMoney(toNum(profil?.synthesePaiement?.montantPayeCdf), 'CDF')}
                </p>
              </div>
            </div>
            <div className="formateur-dash-stat formateur-dash-stat--primary">
              <span className="formateur-dash-stat__icon">
                <LucideSymbolIcon name="school" tone="inherit" size={18} />
              </span>
              <div>
                <p className="formateur-dash-stat__label">Impact cohortes</p>
                <p className="formateur-dash-stat__value">
                  {loading ? '…' : moyenneImpact != null ? `${moyenneImpact}/20` : '—'}
                </p>
                <p className="formateur-dash-stat__hint">
                  {profil?.impactAcademique?.synthese?.nombreCohortes ?? 0} cohorte(s)
                </p>
              </div>
            </div>
            <div className="formateur-dash-stat formateur-dash-stat--amber">
              <span className="formateur-dash-stat__icon">
                <LucideSymbolIcon name="pending" tone="inherit" size={18} />
              </span>
              <div>
                <p className="formateur-dash-stat__label">Montant dû</p>
                <p className="formateur-dash-stat__value formateur-dash-stat__value--sm">
                  {loading ? '…' : formatMoney(montantDu, 'CDF')}
                </p>
              </div>
            </div>
          </div>

          {recentPrestations.length === 0 ? (
            <p className="formateur-dash-empty formateur-dash-empty--inline">
              {loading ? 'Chargement…' : 'Aucune prestation enregistrée.'}
            </p>
          ) : (
            <ul className="formateur-dash-prestations">
              <li className="formateur-dash-prestations__label">Dernières prestations</li>
              {recentPrestations.map((p) => (
                <li key={p.id} className="formateur-dash-prestation">
                  <div className="formateur-dash-prestation__main">
                    <p className="formateur-dash-prestation__date">
                      {formatShortDate(p.datePrestation) ?? '—'}
                    </p>
                    <span
                      className={`formateur-dash-prestation__badge formateur-dash-prestation__badge--${p.statut === 'PAYEE' ? 'paid' : p.statut === 'PLANIFIEE' ? 'pending' : 'default'}`}
                    >
                      {prestationStatutLabel(p.statut)}
                    </span>
                  </div>
                  <p className="formateur-dash-prestation__amount">
                    {formatMoney(toNum(p.montantTotalCdf), 'CDF')}
                  </p>
                </li>
              ))}
            </ul>
          )}

          <Link to="/formateur/performance" className="formateur-dash-widget__footer-link">
            Voir l’impact académique
            <LucideSymbolIcon name="arrow_forward" tone="inherit" size={14} />
          </Link>
        </DashboardWidget>
      </div>
    </FormateurPageShell>
  );
}
